/* eslint-disable max-lines */
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import LoadingModule from 'common/components/LoadingModule';
import { getPrescriptionAttestationApi } from 'api/patient';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { useDispatch } from 'react-redux';

const ComparisonField = ({ label, leftValue, rightValue, rightLabel }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      mb: 1.5,
    }}
  >
    <Grid container>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          borderRight: '1px solid',
          borderColor: 'divider',
          p: 1.5,
        }}
      >
        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
          {leftValue || 'N/A'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={6} sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
          {rightLabel || label}
        </Typography>
        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
          {rightValue || 'N/A'}
        </Typography>
      </Grid>
    </Grid>
  </Box>
);

ComparisonField.propTypes = {
  label: PropTypes.string.isRequired,
  leftValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rightValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rightLabel: PropTypes.string,
};

ComparisonField.defaultProps = {
  leftValue: null,
  rightValue: null,
  rightLabel: null,
};

const AttestationDetailsModal = ({
  open,
  onClose,
  attestationId,
  partnerName,
  proxyPartnerName,
  prescription,
  originalPrescriber,
  practice,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [attestationData, setAttestationData] = useState(null);

  useEffect(() => {
    if (open && attestationId) {
      setLoading(true);
      getPrescriptionAttestationApi({ attestationId })
        .then((data) => {
          setAttestationData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          dispatch(handleError({ error: err }));
        });
    } else if (!open) {
      setAttestationData(null);
    }
  }, [open, attestationId, dispatch]);

  const handleClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    const line1 = address.addressLine1 || address.line1;
    const line2 = address.addressLine2 || address.line2;
    const city = address.addressCity || address.city;
    const state = address.addressState || address.state;
    const zip = address.addressZip || address.zip;
    const parts = [line1];
    if (line2) parts.push(line2);
    if (city || state || zip) {
      const cityStateZip = [city, state, zip].filter(Boolean).join(' ');
      if (cityStateZip) parts.push(cityStateZip);
    }
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Grid container justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
              Attestation Details
            </Typography>
            {partnerName && proxyPartnerName && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5, fontSize: '0.85rem' }}
              >
                {proxyPartnerName} has submitted attestation for {partnerName}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label='Close Attestation Details' size='small'>
            <CloseIcon />
          </IconButton>
        </Grid>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ overflow: 'auto', p: 2, bgcolor: 'grey.100' }}>
        {loading && <LoadingModule height='400px' />}

        {!loading && attestationData && (
          <Grid container spacing={3}>
            {attestationData.patientDemographics && (
              <Grid item xs={12}>
                <Card elevation={3} sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                      Patient Demographics
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          First Name
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {attestationData.patientDemographics.firstName || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Last Name
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {attestationData.patientDemographics.lastName || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Date of Birth
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {formatDate(attestationData.patientDemographics.dob)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Gender
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {attestationData.patientDemographics.gender || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                          Phone Number
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          {attestationData.patientDemographics.phoneNumber || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {attestationData.prescriptionInfo && !isEmpty(attestationData.prescriptionInfo) && (
              <Grid item xs={12}>
                <Card elevation={3} sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, fontSize: '0.85rem' }}>
                      Prescriptions ({attestationData.prescriptionInfo.length})
                    </Typography>

                    {attestationData.prescriptionInfo.map((attestationPrescription, index) => {
                      const prescriptionKey = `${attestationPrescription.description || ''}-${
                        attestationPrescription.strength || ''
                      }-${attestationPrescription.quantity || ''}-${index}`;
                      const isLastPrescription =
                        index === attestationData.prescriptionInfo.length - 1;

                      return (
                        <Box key={prescriptionKey} sx={{ mb: isLastPrescription ? 0 : 4 }}>
                          <Divider sx={{ mb: 3 }} />

                          <Grid container spacing={0} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant='subtitle2'
                                sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                              >
                                Prescription as Written
                              </Typography>
                              <Typography
                                variant='body2'
                                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}
                              >
                                Original from prescriber
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant='subtitle2'
                                sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                              >
                                Prescription Attestation Received
                              </Typography>
                              <Typography
                                variant='body2'
                                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}
                              >
                                Attestation data received
                              </Typography>
                            </Grid>
                          </Grid>

                          <ComparisonField
                            label='Description'
                            leftValue={prescription?.drug || 'N/A'}
                            rightValue={attestationPrescription.description}
                          />

                          <ComparisonField
                            label='Strength'
                            leftValue={prescription?.strength || 'N/A'}
                            rightValue={attestationPrescription.strength}
                          />

                          <ComparisonField
                            label='Quantity'
                            leftValue={prescription?.quantityPrescribed || 'N/A'}
                            rightValue={attestationPrescription.quantity}
                          />

                          <ComparisonField
                            label='Directions'
                            leftValue={
                              prescription?.sig?.sigText || prescription?.directions || 'N/A'
                            }
                            rightValue={attestationPrescription.directions}
                          />

                          <ComparisonField
                            label='Total Refills'
                            leftValue={prescription?.refillsAuthorized || 'N/A'}
                            rightValue={attestationPrescription.totalRefills}
                          />

                          {attestationPrescription.prescribedBy && (
                            <Box sx={{ mt: 3 }}>
                              <Typography
                                variant='subtitle2'
                                sx={{ fontWeight: 600, mb: 2, fontSize: '0.85rem' }}
                              >
                                Prescriber Information
                              </Typography>

                              {(() => {
                                const getPrescriberName = (prescriberData) => {
                                  if (!prescriberData) return null;
                                  if (prescriberData.name) return prescriberData.name;
                                  const parts = [
                                    prescriberData.title,
                                    prescriberData.firstName,
                                    prescriberData.middleName,
                                    prescriberData.lastName,
                                  ].filter(Boolean);
                                  return parts.length > 0 ? parts.join(' ') : null;
                                };

                                const prescriberName =
                                  getPrescriberName(originalPrescriber) || practice?.prescriberName;
                                const prescriberNpi =
                                  originalPrescriber?.npi || practice?.prescriberNpi;
                                const prescriberPhone = originalPrescriber?.phoneNumber;
                                const prescriberAddress = originalPrescriber?.address
                                  ? formatAddress(originalPrescriber.address)
                                  : 'N/A';

                                return (
                                  <>
                                    <ComparisonField
                                      label='Prescriber Name'
                                      leftValue={prescriberName || 'N/A'}
                                      rightValue={`${
                                        attestationPrescription.prescribedBy.firstName || ''
                                      } ${
                                        attestationPrescription.prescribedBy.lastName || ''
                                      }`.trim()}
                                    />

                                    <ComparisonField
                                      label='NPI'
                                      leftValue={prescriberNpi || 'N/A'}
                                      rightValue={attestationPrescription.prescribedBy.npi}
                                    />

                                    <ComparisonField
                                      label='Phone Number'
                                      leftValue={prescriberPhone || 'N/A'}
                                      rightValue={attestationPrescription.prescribedBy.phoneNumber}
                                    />

                                    {/* POM-6354: both sides are practice addresses; a null
                                        attestation address shows N/A instead of hiding the row. */}
                                    <ComparisonField
                                      label='Address'
                                      leftValue={prescriberAddress}
                                      rightValue={formatAddress(
                                        attestationPrescription.prescribedBy.address
                                      )}
                                    />
                                  </>
                                );
                              })()}
                            </Box>
                          )}

                          {attestationPrescription.transferDetails && (
                            <Box sx={{ mt: 3 }}>
                              <Typography
                                variant='subtitle2'
                                sx={{ fontWeight: 600, mb: 2, fontSize: '0.85rem' }}
                              >
                                Transfer Details
                              </Typography>

                              <ComparisonField
                                label='Written Date'
                                leftValue={
                                  prescription?.writtenDate
                                    ? formatDate(prescription.writtenDate)
                                    : 'N/A'
                                }
                                rightValue={formatDate(
                                  attestationPrescription.transferDetails.writtenDate
                                )}
                              />
                            </Box>
                          )}

                          {attestationPrescription.transferDetails?.toPharmacy && (
                            <Box sx={{ mt: 3 }}>
                              <Typography
                                variant='subtitle2'
                                sx={{ fontWeight: 600, mb: 2, fontSize: '0.85rem' }}
                              >
                                Pharmacy Details
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={2.4}>
                                  <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                    Pharmacy Name
                                  </Typography>
                                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                    {attestationPrescription.transferDetails.toPharmacy.name ||
                                      'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                  <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                    NPI
                                  </Typography>
                                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                    {attestationPrescription.transferDetails.toPharmacy.npi ||
                                      'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                  <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                    DEA
                                  </Typography>
                                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                    {attestationPrescription.transferDetails.toPharmacy.dea ||
                                      'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                  <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                    Phone Number
                                  </Typography>
                                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                    {attestationPrescription.transferDetails.toPharmacy
                                      .phoneNumber || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.4}>
                                  <Typography variant='body2' sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                    Address
                                  </Typography>
                                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                                    {attestationPrescription.transferDetails.toPharmacy
                                      .pharmacyAddress
                                      ? formatAddress(
                                          attestationPrescription.transferDetails.toPharmacy
                                            .pharmacyAddress
                                        )
                                      : 'N/A'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          )}

                          {!isLastPrescription && <Divider sx={{ mt: 4 }} />}
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {!attestationData.patientDemographics &&
              (!attestationData.prescriptionInfo || isEmpty(attestationData.prescriptionInfo)) && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px',
                    }}
                  >
                    <Typography variant='body1' color='text.secondary'>
                      No attestation data available
                    </Typography>
                  </Box>
                </Grid>
              )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant='contained' sx={{ minWidth: 100 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AttestationDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attestationId: PropTypes.string.isRequired,
  partnerName: PropTypes.string,
  proxyPartnerName: PropTypes.string,
  prescription: PropTypes.shape({
    drug: PropTypes.string,
    strength: PropTypes.string,
    quantityPrescribed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    directions: PropTypes.string,
    refillsAuthorized: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    writtenDate: PropTypes.string,
    sig: PropTypes.shape({
      sigText: PropTypes.string,
    }),
    request: PropTypes.shape({
      medication: PropTypes.shape({
        prescriber: PropTypes.shape({
          firstName: PropTypes.string,
          lastName: PropTypes.string,
          npi: PropTypes.string,
          phone: PropTypes.string,
          addressLine1: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          zip: PropTypes.string,
        }),
      }),
    }),
  }),
  originalPrescriber: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    middleName: PropTypes.string,
    title: PropTypes.string,
    npi: PropTypes.string,
    phoneNumber: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
  }),
  practice: PropTypes.shape({
    prescriberName: PropTypes.string,
    prescriberNpi: PropTypes.string,
  }),
};

AttestationDetailsModal.defaultProps = {
  partnerName: null,
  proxyPartnerName: null,
  prescription: null,
  originalPrescriber: null,
  practice: null,
};

export default AttestationDetailsModal;
